/* eslint-disable no-param-reassign */
import gameInfo from '../assets/json/gameInfo.json';
import Phaser from 'phaser';
import gameData, { GameShortNames } from './gameData.js';
import FontKeys from './FontKeys';
import ABCyaLib from '@abcya/abcya_lib';
import ABCyaPhaser from '@abcya/abcya_phaser';
import setupGameConfig from './GameConfig';

const fonts = [];
for (const k in FontKeys) {
  fonts.push(FontKeys[k]);
}

// note: uncomment or change the following based on what you need to load
ABCyaPhaser.Preloader.PreloaderConfig.fonts = fonts;

/**
 * To load multiple image sprites, change this property to an array of Phaser.Types.Loader.FileTypes.MultiAtlasFileConfig objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = [
 * { key: ABCyaPhaser.Images.DEFAULT_ATLAS, atlasURL: 'assets/images/' + ABCyaPhaser.Images.DEFAULT_ATLAS + '.json', path: 'assets/images' }, // default multiatlas
 * { key: "particles", atlasURL: 'assets/images/particles.json', path: 'assets/images' }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = 'assets/images';

/**
 * To load multiple audio sprites, change this property to an array of Phaser.Types.Loader.FileTypes.AudioSpriteFileConfig objects.
 * Note: you should always include an ogg and mp3 file, the game will determine which to use based on compatibility. 
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = [
 * { key: ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE, 
 *   jsonURL: 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.json', 
 *   audioURL: [ 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.ogg', 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.mp3' ] }, // default sound sprite
 * { key: "extrasounds", jsonURL: 'assets/sounds/extrasounds.json', audioURL: [ 'assets/sounds/extrasounds.ogg', 'assets/sounds/extrasounds.mp3' ] }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = [
  { key: ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE,
    jsonURL: `assets/sounds/${ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE}.json`,
    audioURL: [`assets/sounds/${ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE}.ogg`, `assets/sounds/${ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE}.mp3`] }, // default sound sprite
  { key: gameData.gameModeSoundAtlas, jsonURL: 'assets/sounds/sounds.json', audioURL: ['assets/sounds/sounds.ogg', 'assets/sounds/sounds.mp3'] }
];

/**
 * You can load spine objects here. This can be an object with key, jsonPath, and atlasPath properties, or an array of those objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.spines = [
 * { key: "spine0", jsonPath: "assets/spines/spine0.json", atlasPath: "assets/spines/spine0.atlas" },
 * { key: "spine1", jsonPath: "assets/spines/spine1.json", atlasPath: "assets/spines/spine1.atlas" }
 * ]
 */
// ABCyaPhaser.Preloader.PreloaderConfig.spines = { key: "", jsonPath: "", atlasPath: "" };

ABCyaPhaser.Preloader.PreloaderConfig.startScene = gameData.startScene;

/**
 * Called when the preload function is done.
 * @param {Phaser.Scene} scene the preloader scene
 */
ABCyaPhaser.Preloader.PreloaderConfig.preloadCallback = (scene) => {
  // insert additional preloader functionality here; scene is the preloader scene
  scene.events.once(Phaser.Scenes.Events.CREATE, () => {
    if (gameData.game === GameShortNames.fg || gameData.game === GameShortNames.fgJr) {
      const fixPivot = (frame, pivot) => {
        frame.customPivot = true;
        frame.pivotX = pivot.x / frame.width;
        frame.pivotY = pivot.y / frame.height;
      };
      const texture = scene.textures.get(ABCyaPhaser.Images.DEFAULT_ATLAS);

      fixPivot(texture.get('walk_0'), { x: -6, y: 0 });
      fixPivot(texture.get('walk_1'), { x: -6, y: 0 });
      fixPivot(texture.get('walk_2'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_3'), { x: -3, y: 0 });
      fixPivot(texture.get('walk_4'), { x: -1, y: 0 });
      fixPivot(texture.get('walk_5'), { x: 0, y: 0 });
      fixPivot(texture.get('walk_6'), { x: 0, y: 0 });
      fixPivot(texture.get('walk_7'), { x: 0, y: 0 });
      fixPivot(texture.get('walk_8'), { x: 0, y: 0 });
      fixPivot(texture.get('walk_9'), { x: -1, y: 0 });
      fixPivot(texture.get('walk_10'), { x: -3, y: 0 });
      fixPivot(texture.get('walk_11'), { x: -7, y: 0 });
      fixPivot(texture.get('walk_12'), { x: -6, y: 0 });
      fixPivot(texture.get('walk_13'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_14'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_15'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_16'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_17'), { x: -5, y: 0 });
      fixPivot(texture.get('walk_18'), { x: -6, y: 0 });
      fixPivot(texture.get('walk_19'), { x: -7, y: 0 });
      fixPivot(texture.get('walk_20'), { x: -8, y: 0 });
      fixPivot(texture.get('walk_21'), { x: -8, y: 0 });
      fixPivot(texture.get('walk_22'), { x: -8, y: 0 });
      fixPivot(texture.get('walk_23'), { x: -6, y: 0 });
      fixPivot(texture.get('idle'), { x: -7, y: 0 });
    }
  });
};

(async function handshake() {
  try {
    await ABCyaLib.Environment.instance.doParentHandshake();
  }
  catch (error) {
    console.warn(error);
  }

  console.info('ABCya Game Info:', gameInfo);

  // check to see which game is being set. default is regular version.
  gameData.game = ABCyaLib.getConfig()?.game ?? gameData.game;
  gameData.STORAGE_PREFIX = gameData.game;

  // setup the game config on the Environment object
  ABCyaLib.Environment.instance.setupGameConfig(setupGameConfig);

  gameData.allLevels = gameData.gameConfigs[gameData.game].destinationData;

  for (let i = 0; i < gameData.allLevels.length; i++) {
    const levelData = gameData.allLevels[i];
    const levelId = levelData.destinationId.toLowerCase();

    levelData.bestTime = parseInt(ABCyaLib.Storage.getObject(`${gameData.STORAGE_PREFIX + levelId}_best`) ?? '0');
    levelData.medal = ABCyaLib.Storage.getObject(`${gameData.STORAGE_PREFIX + levelId}_medal`) ?? '';
  }

  /**
     * Use the fourth argument of initializePhaserGame to override default Phaser config options
     * For example: to use the Spine plugin, the fourth argument should look like this:
     * { 
            configOverrides: {
                plugins: {
                    scene: [
                        { key: 'SpinePlugin', plugin: window.SpinePlugin, mapping: 'spine' }
                    ]
                }
            }
        }
     */
  ABCyaPhaser.Misc.initializePhaserGame(768, 1024, gameData.scenes);

  // initialize any functionality in abcya_phaser that has a dependency on abcya_lib 
  ABCyaPhaser.Misc.initializeAbcyaLibDependencies(ABCyaLib);
}());
